import {getApiUrl} from "../config/api";

class PropertyTypeIconService {
  private baseUrl = getApiUrl();

  // SSL error detection and fallback
  private async makeRequest(
    url: string,
    options: RequestInit
  ): Promise<Response> {
    try {
      console.log(`🌐 Attempting request to: ${url}`);
      const response = await fetch(url, options);
      return response;
    } catch (error: any) {
      console.error(`❌ Request failed:`, error);

      // Check if it's an SSL error
      if (
        error.message?.includes("SSL") ||
        error.message?.includes("certificate") ||
        error.message?.includes("ERR_SSL_UNRECOGNIZED_NAME_ALERT")
      ) {
        console.log("🔒 SSL Error detected, trying HTTP fallback...");

        // Try HTTP fallback for development
        if (import.meta.env.MODE === "development") {
          const httpUrl = url.replace("https://", "http://");
          console.log(`🔄 Retrying with HTTP: ${httpUrl}`);
          return fetch(httpUrl, options);
        }
      }

      throw error;
    }
  }

  async uploadIcon(
    propertyTypeId: number,
    file: File
  ): Promise<any> {
    const formData = new FormData();
    formData.append("icon", file);
    
    console.log(
      `📁 Uploading icon: ${file.name} (${file.size} bytes, ${file.type})`
    );

    const token = this.getAuthToken();
    const url = `${this.baseUrl}/property-types/${propertyTypeId}/icon`;

    console.log(`📤 Upload URL: ${url}`);
    console.log(`🔑 Token available: ${!!token}`);

    try {
      const response = await this.makeRequest(url, {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          // Don't set Content-Type for FormData
        },
        body: formData,
      });

      console.log(
        `📨 Response status: ${response.status} ${response.statusText}`
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Error response: ${errorText}`);
        throw new Error(`Icon upload failed: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      console.log(`✅ Icon upload successful:`, result);
      return result;
    } catch (error) {
      console.error("❌ Icon upload error:", error);
      throw error;
    }
  }

  async deleteIcon(propertyTypeId: number): Promise<boolean> {
    const token = this.getAuthToken();
    const url = `${this.baseUrl}/property-types/${propertyTypeId}/icon`;

    console.log(`🗑️ Delete icon URL: ${url}`);

    try {
      const response = await this.makeRequest(url, {
        method: "DELETE",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "Content-Type": "application/json",
        },
      });

      console.log(
        `📨 Delete response status: ${response.status} ${response.statusText}`
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Delete failed: ${response.status}`);
      }

      console.log(`✅ Icon deleted successfully`);
      return true;
    } catch (error) {
      console.error("❌ Icon delete error:", error);
      throw error;
    }
  }

  private getAuthToken(): string | null {
    try {
      const persistedState = localStorage.getItem("persist:root");
      if (persistedState) {
        const parsed = JSON.parse(persistedState);
        const auth = JSON.parse(parsed.auth || "{}");
        if (auth.token) {
          return auth.token;
        }
      }

      // Check direct localStorage
      const directToken = localStorage.getItem("auth_token");
      if (directToken) {
        return directToken;
      }

      return null;
    } catch (error) {
      console.error("❌ Failed to get auth token:", error);
      return null;
    }
  }
}

export const propertyTypeIconService = new PropertyTypeIconService();